// Audio-type taxonomy for script rows.
//
// Every AUDIO script cell can carry an `audioType` attribute describing *how*
// the line is heard: narration, a presenter to camera, an interview answer, or
// no audio at all (on-screen text). It rides inside the doc_json as a
// ProseMirror node attribute, so it flows to the client review page, the PDF
// and the diff for free.
//
// `null` means "untyped" — existing rows authored before this feature, and any
// row the producer hasn't tagged yet. Untyped rows render exactly as they did
// before (a plain "A" marker, no chip), so no already-approved script changes
// under a client. Newly-created rows are seeded with DEFAULT_AUDIO_TYPE.
//
// Framework-free so the worker (PDF, runtime badge) and the client editor can
// share one source of truth.

export type AudioType = 'voiceover' | 'to_camera' | 'interview' | 'silent';

export const AUDIO_TYPES: readonly AudioType[] = [
  'voiceover',
  'to_camera',
  'interview',
  'silent',
];

/** The type a brand-new row is created with. Untagged existing rows stay
 *  `null` (untyped) — see the module note above. */
export const DEFAULT_AUDIO_TYPE: AudioType = 'voiceover';

export interface AudioTypeMeta {
  type: AudioType;
  /** Short label shown on the chip and in the picker. */
  label: string;
  /** One-line description — the picker row's subtitle and the chip's tooltip. */
  gloss: string;
}

export const AUDIO_TYPE_META: Record<AudioType, AudioTypeMeta> = {
  voiceover: {
    type: 'voiceover',
    label: 'Voiceover',
    gloss: 'Narration over visuals — no speaker on screen.',
  },
  to_camera: {
    type: 'to_camera',
    label: 'To camera',
    gloss: 'Presenter speaks directly to the lens (a “piece to camera”).',
  },
  interview: {
    type: 'interview',
    label: 'Interview',
    gloss: 'Sync sound from an interview or talking-head answer.',
  },
  silent: {
    type: 'silent',
    label: 'On-screen text',
    gloss: 'No spoken audio — on-screen text or graphics carry it.',
  },
};

export function isAudioType(value: unknown): value is AudioType {
  return typeof value === 'string' && (AUDIO_TYPES as readonly string[]).includes(value);
}

/** Coerce an unknown attribute value to a valid AudioType, or `null` (untyped)
 *  for anything unrecognised — including the empty string and legacy docs. */
export function normalizeAudioType(value: unknown): AudioType | null {
  return isAudioType(value) ? value : null;
}

/** Whether a line of this type is spoken aloud (and so counts toward the
 *  on-camera runtime estimate). Only on-screen text is silent. Untyped rows
 *  (`null`) are treated as spoken — they predate the feature and default to
 *  ordinary narration. */
export function isSpokenAudioType(value: unknown): boolean {
  return normalizeAudioType(value) !== 'silent';
}
